import { FC, useMemo, useState } from "react";
import { Card, Tag, Empty, Button, Modal } from "antd";
import { Calendar, ChevronLeft, ChevronRight, MapPin, Users, Package } from "lucide-react";
import { capitalize } from "~/utils";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { TServiceTicket } from "~/types";
import { JobStatus } from "~/utils/constants";
import { ContainerInfoCard } from "~/components/common/container-info";

dayjs.extend(isoWeek);

type Props = {
  serviceTickets: TServiceTicket[];
};

export const ServicesCalendarView: FC<Props> = ({ serviceTickets }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(dayjs().startOf("isoWeek"));
  const [selectedService, setSelectedService] = useState<any | null>(null);

  // Group tickets by date
  const ticketsByDate = useMemo(() => {
    const grouped: Record<string, any[]> = {};

    serviceTickets.forEach((ticket) => {
      const date = dayjs(ticket.serviceDatetime || ticket.startDatetime).format("YYYY-MM-DD");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(ticket);
    });

    // Sort tickets within each date by time
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => {
        const timeA = dayjs(a.serviceDatetime || a.startDatetime);
        const timeB = dayjs(b.serviceDatetime || b.startDatetime);
        return timeA.diff(timeB);
      });
    });

    return grouped;
  }, [serviceTickets]);

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      [JobStatus.COMPLETED]: "success",
      [JobStatus.IN_PROGRESS]: "processing",
      [JobStatus.SCHEDULED]: "blue",
      [JobStatus.DRAFT]: "default",
      [JobStatus.CANCELLED]: "error",
      [JobStatus.ESTIMATED]: "warning",
    };
    return statusMap[status] || "default";
  };

  const navigateWeeks = (direction: "prev" | "next") => {
    if (direction === "prev") {
      setCurrentWeekStart(currentWeekStart.subtract(2, "week"));
    } else {
      setCurrentWeekStart(currentWeekStart.add(2, "week"));
    }
  };

  const goToToday = () => {
    setCurrentWeekStart(dayjs().startOf("isoWeek"));
  };

  // Generate 14 days starting from currentWeekStart
  const twoWeekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 14; i++) {
      days.push(currentWeekStart.add(i, "day"));
    }
    return days;
  }, [currentWeekStart]);

  const endDate = currentWeekStart.add(13, "day");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="text-blue-600" size={24} />
          <div>
            <h3 className="text-lg font-semibold">Calendar View</h3>
            <p className="text-sm text-gray-500">
              {currentWeekStart.format("MMM D")} - {endDate.format("MMM D, YYYY")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={() => navigateWeeks("prev")} icon={<ChevronLeft size={16} />}>
            Previous
          </Button>
          <Button onClick={goToToday}>Today</Button>
          <Button onClick={() => navigateWeeks("next")} icon={<ChevronRight size={16} />}>
            Next
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-3">
        {twoWeekDays.map((day) => {
          const dateKey = day.format("YYYY-MM-DD");
          const tickets = ticketsByDate[dateKey] || [];
          const isToday = day.isSame(dayjs(), "day");
          const isPast = day.isBefore(dayjs(), "day");

          return (
            <div
              key={dateKey}
              className={`bg-white border rounded-lg p-2 min-h-[180px] ${
                isToday ? "border-blue-500 border-2" : ""
              } ${isPast ? "bg-gray-50" : ""}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className={`text-xs font-medium ${isToday ? "text-blue-600" : "text-gray-600"}`}>
                    {day.format("ddd")}
                  </div>
                  <div
                    className={`text-xl font-bold ${isToday ? "text-blue-600" : isPast ? "text-gray-400" : "text-gray-900"}`}
                  >
                    {day.format("D")}
                  </div>
                </div>
                {tickets.length > 0 && (
                  <div className="bg-blue-100 text-blue-600 text-xs font-medium px-1.5 py-0.5 rounded-full">
                    {tickets.length}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {tickets.length === 0 ? (
                  <div className="text-xs text-gray-400 text-center py-4">No services</div>
                ) : (
                  tickets.map((ticket) => {
                    // Combine date from serviceDatetime with time from startDatetime
                    let displayTime;
                    if (ticket.serviceDatetime && ticket.startDatetime) {
                      const serviceDate = dayjs(ticket.serviceDatetime);
                      const startTimeData = dayjs(ticket.startDatetime);
                      displayTime = serviceDate
                        .hour(startTimeData.hour())
                        .minute(startTimeData.minute())
                        .second(startTimeData.second());
                    } else {
                      displayTime = dayjs(ticket.serviceDatetime || ticket.startDatetime);
                    }

                    return (
                      <Card
                        key={ticket.id}
                        size="small"
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        bodyStyle={{ padding: "8px" }}
                        onClick={() => setSelectedService(ticket)}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-blue-600">{ticket.serial}</span>
                            <Tag color={getStatusColor(ticket.status)} className="text-xs">
                              {capitalize(ticket.status || "pending")}
                            </Tag>
                          </div>

                          {/* Service Client */}
                          <div className="text-xs text-gray-900 font-medium truncate">
                            {ticket.clientService?.companyName ||
                             ticket.client?.companyName ||
                             `${ticket.clientService?.firstName || ticket.client?.firstName || ""} ${ticket.clientService?.lastName || ticket.client?.lastName || ""}`.trim() ||
                             "Client N/A"}
                          </div>

                          <div className="text-xs font-medium text-gray-700">
                            {displayTime.format("h:mm A")}
                          </div>

                          <div className="text-xs text-gray-600 truncate">
                            {capitalize(ticket.serviceType)}
                          </div>

                          {ticket.location && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <MapPin size={10} />
                              <span className="truncate">
                                {[ticket.location.city, ticket.location.state]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {ticket.staffs && ticket.staffs.length > 0 && (
                              <div className="flex items-center gap-1">
                                <Users size={10} />
                                <span>{ticket.staffs.length}</span>
                              </div>
                            )}
                            {ticket.inventories && ticket.inventories.length > 0 && (
                              <div className="flex items-center gap-1">
                                <Package size={10} />
                                <span>{ticket.inventories.length}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        title="Service Details"
        open={!!selectedService}
        onCancel={() => setSelectedService(null)}
        footer={null}
        width={800}
      >
        {selectedService && (
          <div className="cursor-pointer">
            <ContainerInfoCard
              serviceTicket={selectedService}
              variant="detailed"
              showHeader={true}
              className="border-0"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
